import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { uploadRecording } from '../services/recordingsService';
import {
  ChevronRight,
  Volume2,
  Mic,
  Square,
  Sparkles,
  Shuffle,
  Flame,
  Play,
  RotateCcw,
  Timer,
  MessageCircle,
  Send,
} from 'lucide-react';

/* -----------------------------------------------------------
   Native fr-FR speech (browser SpeechSynthesis).
----------------------------------------------------------- */
const speak = (text, rate = 0.95) => {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  try {
    window.speechSynthesis.cancel();
    const u = new window.SpeechSynthesisUtterance(text);
    u.lang = 'fr-FR';
    u.rate = rate;
    window.speechSynthesis.speak(u);
  } catch {
    /* silent */
  }
};

/* -----------------------------------------------------------
   20 A1 speaking missions
----------------------------------------------------------- */
const MISSIONS_A1 = [
  {
    id: 'm1', emoji: '☀️', title: 'Ma routine du matin',
    clues: [{ e: '⏰', t: 'heure' }, { e: '🥐', t: 'petit-déjeuner' }, { e: '🪥', t: 'préparation' }, { e: '🚶', t: 'départ' }],
    mission: 'Parle de ta routine du matin pendant 20 secondes.',
    nativeAlt: 'Au lieu de « Je me lève à 7h », essaie : « Le matin, je suis du genre matinal — debout à 7h pile. »',
  },
  {
    id: 'm2', emoji: '👨‍👩‍👧', title: 'Ma famille',
    clues: [{ e: '👩', t: 'mère' }, { e: '👨', t: 'père' }, { e: '👧', t: 'frères/sœurs' }, { e: '❤️', t: 'activité ensemble' }],
    mission: 'Présente ta famille.',
    nativeAlt: 'Au lieu de « J\'ai une famille », essaie : « Ma famille, c\'est mon petit cocon. »',
  },
  {
    id: 'm3', emoji: '🐶', title: 'Mon animal préféré',
    clues: [{ e: '🐾', t: 'animal' }, { e: '🎨', t: 'couleur' }, { e: '😊', t: 'caractère' }, { e: '❤️', t: 'pourquoi' }],
    mission: 'Décris ton animal préféré.',
    nativeAlt: 'Au lieu de « J\'aime les chiens », essaie : « Les chiens, j\'en suis fou — ils ont un truc rassurant. »',
  },
  {
    id: 'm4', emoji: '🍕', title: 'Mon repas préféré',
    clues: [{ e: '🍽️', t: 'nourriture' }, { e: '😋', t: 'goût' }, { e: '📍', t: 'où' }, { e: '👫', t: 'avec qui' }],
    mission: 'Parle de ton repas préféré.',
    nativeAlt: 'Au lieu de « J\'aime la pizza », essaie : « Je suis incapable de résister à une bonne pizza. »',
  },
  {
    id: 'm5', emoji: '🏫', title: 'Mon école / mon travail',
    clues: [{ e: '🏢', t: 'lieu' }, { e: '📚', t: 'activité' }, { e: '👥', t: 'personnes' }, { e: '😊', t: 'opinion' }],
    mission: 'Décris ton école ou ton travail.',
    nativeAlt: 'Au lieu de « Je travaille dans un bureau », essaie : « Je bosse dans un bureau plutôt sympa. »',
  },
  {
    id: 'm6', emoji: '🌦️', title: 'Le temps aujourd\'hui',
    clues: [{ e: '☀️', t: 'météo' }, { e: '🌡️', t: 'température' }, { e: '👕', t: 'vêtements' }, { e: '😊', t: 'sentiment' }],
    mission: 'Parle du temps aujourd\'hui.',
    nativeAlt: 'Au lieu de « Il fait froid », essaie : « Aujourd\'hui, il fait un froid de canard. »',
  },
  {
    id: 'm7', emoji: '🎵', title: 'La musique que j\'aime',
    clues: [{ e: '🎤', t: 'style' }, { e: '🎧', t: 'quand' }, { e: '😊', t: 'émotion' }, { e: '⭐', t: 'artiste' }],
    mission: 'Parle de la musique que tu aimes.',
    nativeAlt: 'Au lieu de « J\'aime la pop », essaie : « La pop, ça me met direct de bonne humeur. »',
  },
  {
    id: 'm8', emoji: '🏠', title: 'Ma maison idéale',
    clues: [{ e: '🏡', t: 'taille' }, { e: '🛏️', t: 'chambres' }, { e: '🌳', t: 'extérieur' }, { e: '❤️', t: 'pourquoi' }],
    mission: 'Décris ta maison idéale.',
    nativeAlt: 'Au lieu de « Ma maison est grande », essaie : « Ma maison de rêve, ce serait un petit nid lumineux. »',
  },
  {
    id: 'm9', emoji: '👗', title: 'Mes vêtements préférés',
    clues: [{ e: '👕', t: 'vêtements' }, { e: '🎨', t: 'couleur' }, { e: '🛍️', t: 'shopping' }, { e: '😊', t: 'pourquoi' }],
    mission: 'Parle de tes vêtements préférés.',
    nativeAlt: 'Au lieu de « J\'aime les jeans », essaie : « Un bon jean, c\'est ma valeur sûre. »',
  },
  {
    id: 'm10', emoji: '🚆', title: 'Mon moyen de transport',
    clues: [{ e: '🚌', t: 'transport' }, { e: '⏰', t: 'fréquence' }, { e: '😊', t: 'opinion' }, { e: '📍', t: 'destination' }],
    mission: 'Explique comment tu te déplaces.',
    nativeAlt: 'Au lieu de « Je prends le métro », essaie : « Je suis un habitué du métro. »',
  },
  {
    id: 'm11', emoji: '📱', title: 'Mon application préférée',
    clues: [{ e: '📲', t: 'application' }, { e: '⏰', t: 'fréquence' }, { e: '🎯', t: 'utilité' }, { e: '❤️', t: 'pourquoi' }],
    mission: 'Parle de ton application préférée.',
    nativeAlt: 'Au lieu de « J\'utilise WhatsApp », essaie : « Je suis accro à WhatsApp, je l\'ouvre cent fois par jour. »',
  },
  {
    id: 'm12', emoji: '🌍', title: 'Le pays que je veux visiter',
    clues: [{ e: '✈️', t: 'pays' }, { e: '🍜', t: 'nourriture' }, { e: '🏖️', t: 'lieux' }, { e: '😊', t: 'raison' }],
    mission: 'Parle d\'un pays que tu veux visiter.',
    nativeAlt: 'Au lieu de « Je veux aller au Japon », essaie : « Le Japon, ça me fait rêver depuis des années. »',
  },
  {
    id: 'm13', emoji: '🎂', title: 'Mon anniversaire idéal',
    clues: [{ e: '🎉', t: 'fête' }, { e: '👫', t: 'invités' }, { e: '🍰', t: 'nourriture' }, { e: '🎁', t: 'cadeaux' }],
    mission: 'Décris ton anniversaire idéal.',
    nativeAlt: 'Au lieu de « C\'est une bonne fête », essaie : « Ce serait une soirée vraiment chouette. »',
  },
  {
    id: 'm14', emoji: '⚽', title: 'Mon loisir préféré',
    clues: [{ e: '🎯', t: 'activité' }, { e: '📍', t: 'où' }, { e: '👥', t: 'avec qui' }, { e: '😊', t: 'sentiment' }],
    mission: 'Parle de ton loisir préféré.',
    nativeAlt: 'Au lieu de « J\'aime le foot », essaie : « Le foot, c\'est ma soupape de décompression. »',
  },
  {
    id: 'm15', emoji: '🛒', title: 'Au supermarché',
    clues: [{ e: '🛍️', t: 'achats' }, { e: '💶', t: 'prix' }, { e: '🥛', t: 'nourriture' }, { e: '😊', t: 'expérience' }],
    mission: 'Décris une visite au supermarché.',
    nativeAlt: 'Au lieu de « J\'achète du pain », essaie : « Je fais un petit tour au rayon boulangerie. »',
  },
  {
    id: 'm16', emoji: '☕', title: 'Au café',
    clues: [{ e: '☕', t: 'boisson' }, { e: '🍰', t: 'nourriture' }, { e: '👥', t: 'compagnie' }, { e: '😊', t: 'ambiance' }],
    mission: 'Parle d\'un moment dans un café.',
    nativeAlt: 'Au lieu de « Je bois un café », essaie : « Je m\'offre un petit café tranquille. »',
  },
  {
    id: 'm17', emoji: '📚', title: 'Mon livre ou film préféré',
    clues: [{ e: '🎬', t: 'type' }, { e: '👤', t: 'personnage' }, { e: '😊', t: 'émotion' }, { e: '❤️', t: 'pourquoi' }],
    mission: 'Présente un livre ou un film que tu aimes.',
    nativeAlt: 'Au lieu de « C\'est un bon film », essaie : « Ce film m\'a vraiment marqué(e). »',
  },
  {
    id: 'm18', emoji: '🌳', title: 'Une journée au parc',
    clues: [{ e: '🌳', t: 'lieu' }, { e: '🚶', t: 'activité' }, { e: '👫', t: 'personnes' }, { e: '☀️', t: 'météo' }],
    mission: 'Décris une journée au parc.',
    nativeAlt: 'Au lieu de « Je marche dans le parc », essaie : « Je me balade tranquillement dans le parc. »',
  },
  {
    id: 'm19', emoji: '📅', title: 'Mon week-end parfait',
    clues: [{ e: '😴', t: 'repos' }, { e: '🍽️', t: 'nourriture' }, { e: '👫', t: 'amis/famille' }, { e: '🎉', t: 'activité' }],
    mission: 'Parle de ton week-end parfait.',
    nativeAlt: 'Au lieu de « Mon week-end est bon », essaie : « Mon week-end idéal, c\'est zéro stress, beaucoup de rires. »',
  },
  {
    id: 'm20', emoji: '💭', title: 'Mes rêves pour le futur',
    clues: [{ e: '💼', t: 'travail' }, { e: '🏡', t: 'vie' }, { e: '✈️', t: 'voyages' }, { e: '😊', t: 'objectif' }],
    mission: 'Parle de ton futur idéal.',
    nativeAlt: 'Au lieu de « Je veux être heureux », essaie : « Mon rêve, c\'est une vie qui me ressemble vraiment. »',
  },
];

/* -----------------------------------------------------------
   20 A2 speaking missions — narration, opinion, connectors
----------------------------------------------------------- */
const MISSIONS_A2 = [
  {
    id: 'a2-m1', emoji: '✈️', title: 'Un voyage mémorable',
    clues: [{ e: '🌍', t: 'destination' }, { e: '🚆', t: 'transport' }, { e: '😄', t: 'expérience' }, { e: '📸', t: 'souvenir' }],
    mission: 'Raconte un voyage que tu as aimé.',
    nativeAlt: 'Au lieu de « C\'était bien », essaie : « Ce voyage m\'a laissé un souvenir mémorable, parce que… »',
  },
  {
    id: 'a2-m2', emoji: '🍽️', title: 'Un restaurant que tu recommandes',
    clues: [{ e: '🍴', t: 'nourriture' }, { e: '👥', t: 'ambiance' }, { e: '💶', t: 'prix' }, { e: '❤️', t: 'pourquoi' }],
    mission: 'Présente un restaurant que tu apprécies.',
    nativeAlt: 'Au lieu de « C\'est un bon restaurant », essaie : « Ce restaurant, c\'est une valeur sûre — la cuisine est généreuse et l\'ambiance chaleureuse. »',
  },
  {
    id: 'a2-m3', emoji: '📱', title: 'Les réseaux sociaux',
    clues: [{ e: '📲', t: 'applications' }, { e: '⏰', t: 'temps' }, { e: '😊', t: 'avantages' }, { e: '⚠️', t: 'inconvénients' }],
    mission: 'Parle de ton utilisation des réseaux sociaux.',
    nativeAlt: 'Au lieu de « J\'utilise Instagram », essaie : « Instagram, je l\'ouvre par réflexe — c\'est pratique pour rester en contact, mais ça mange beaucoup de temps. »',
  },
  {
    id: 'a2-m4', emoji: '🎓', title: 'Ton professeur idéal',
    clues: [{ e: '👨‍🏫', t: 'personnalité' }, { e: '📚', t: 'méthode' }, { e: '😊', t: 'qualités' }, { e: '💡', t: 'importance' }],
    mission: 'Décris le professeur idéal selon toi.',
    nativeAlt: 'Au lieu de « Un bon prof est gentil », essaie : « Un bon prof, c\'est quelqu\'un qui donne envie d\'apprendre, parce qu\'il fait confiance à ses élèves. »',
  },
  {
    id: 'a2-m5', emoji: '🏙️', title: 'La ville où tu aimerais vivre',
    clues: [{ e: '🏢', t: 'ville' }, { e: '🌳', t: 'environnement' }, { e: '🚇', t: 'transports' }, { e: '❤️', t: 'raisons' }],
    mission: 'Parle d\'une ville où tu aimerais habiter.',
    nativeAlt: 'Au lieu de « Lisbonne est belle », essaie : « Lisbonne me fait rêver, surtout parce qu\'on y respire un mélange de calme et de vie. »',
  },
  {
    id: 'a2-m6', emoji: '🛍️', title: 'Une mauvaise expérience de shopping',
    clues: [{ e: '🛒', t: 'magasin' }, { e: '😕', t: 'problème' }, { e: '🗣️', t: 'réaction' }, { e: '✅', t: 'solution' }],
    mission: 'Raconte une mauvaise expérience de shopping.',
    nativeAlt: 'Au lieu de « Le vendeur n\'était pas gentil », essaie : « Le vendeur n\'a pas du tout été à l\'écoute, donc j\'ai préféré partir et aller ailleurs. »',
  },
  {
    id: 'a2-m7', emoji: '🎬', title: 'Une série ou un film intéressant',
    clues: [{ e: '🎥', t: 'histoire' }, { e: '👤', t: 'personnages' }, { e: '😲', t: 'émotion' }, { e: '⭐', t: 'recommandation' }],
    mission: 'Présente une série ou un film que tu recommandes.',
    nativeAlt: 'Au lieu de « C\'est une bonne série », essaie : « Cette série m\'a complètement embarqué(e) — les personnages sont si humains qu\'on s\'y attache vite. »',
  },
  {
    id: 'a2-m8', emoji: '🌦️', title: 'Les saisons que tu préfères',
    clues: [{ e: '☀️', t: 'météo' }, { e: '👕', t: 'vêtements' }, { e: '🎉', t: 'activités' }, { e: '😊', t: 'sentiment' }],
    mission: 'Compare tes saisons préférées.',
    nativeAlt: 'Au lieu de « J\'aime l\'été », essaie : « J\'ai un faible pour l\'été parce que tout devient plus lumineux, mais l\'automne a aussi son charme. »',
  },
  {
    id: 'a2-m9', emoji: '💼', title: 'Ton métier idéal',
    clues: [{ e: '🏢', t: 'profession' }, { e: '💰', t: 'avantages' }, { e: '😊', t: 'motivation' }, { e: '📅', t: 'futur' }],
    mission: 'Parle du métier que tu voudrais faire.',
    nativeAlt: 'Au lieu de « Je veux être journaliste », essaie : « Ce qui m\'attire vraiment, c\'est le journalisme — j\'aurais l\'impression de faire quelque chose d\'utile. »',
  },
  {
    id: 'a2-m10', emoji: '📚', title: 'Une matière difficile',
    clues: [{ e: '📖', t: 'matière' }, { e: '😵', t: 'difficulté' }, { e: '📝', t: 'solutions' }, { e: '🎯', t: 'objectif' }],
    mission: 'Explique une matière difficile pour toi.',
    nativeAlt: 'Au lieu de « Les maths sont difficiles », essaie : « Les maths me donnent du fil à retordre, mais je m\'y attelle régulièrement pour progresser. »',
  },
  {
    id: 'a2-m11', emoji: '🚲', title: 'Les transports écologiques',
    clues: [{ e: '🚲', t: 'exemples' }, { e: '🌍', t: 'environnement' }, { e: '👍', t: 'avantages' }, { e: '👎', t: 'difficultés' }],
    mission: 'Parle des transports écologiques.',
    nativeAlt: 'Au lieu de « Le vélo est bon pour la planète », essaie : « Le vélo, c\'est à la fois bon pour la santé et pour la planète, cependant ce n\'est pas toujours pratique en hiver. »',
  },
  {
    id: 'a2-m12', emoji: '🎉', title: 'Une fête réussie',
    clues: [{ e: '🎂', t: 'événement' }, { e: '👫', t: 'invités' }, { e: '🍕', t: 'nourriture' }, { e: '😄', t: 'ambiance' }],
    mission: 'Décris une fête réussie.',
    nativeAlt: 'Au lieu de « C\'était une bonne fête », essaie : « Cette soirée était une vraie réussite — tout le monde a fini par danser jusqu\'au bout de la nuit. »',
  },
  {
    id: 'a2-m13', emoji: '🧑‍🍳', title: 'Cuisiner ou manger dehors ?',
    clues: [{ e: '🍳', t: 'cuisine' }, { e: '🍔', t: 'restaurant' }, { e: '💶', t: 'coût' }, { e: '😊', t: 'préférence' }],
    mission: 'Compare cuisiner à la maison et manger dehors.',
    nativeAlt: 'Au lieu de « J\'aime cuisiner », essaie : « Cuisiner me détend après une longue journée, alors que sortir au resto, c\'est plus pour les occasions. »',
  },
  {
    id: 'a2-m14', emoji: '🧒', title: 'Ton enfance',
    clues: [{ e: '🏫', t: 'école' }, { e: '🎮', t: 'loisirs' }, { e: '👫', t: 'amis' }, { e: '😊', t: 'souvenirs' }],
    mission: 'Parle de quelques souvenirs d\'enfance.',
    nativeAlt: 'Au lieu de « J\'ai eu une bonne enfance », essaie : « Mon enfance, c\'est surtout des étés passés dehors avec les copains — des souvenirs qui me reviennent en flashs. »',
  },
  {
    id: 'a2-m15', emoji: '🌍', title: 'Les langues étrangères',
    clues: [{ e: '🗣️', t: 'langues' }, { e: '📚', t: 'apprentissage' }, { e: '✈️', t: 'voyages' }, { e: '💼', t: 'utilité' }],
    mission: 'Explique pourquoi apprendre des langues est important.',
    nativeAlt: 'Au lieu de « C\'est bien d\'apprendre des langues », essaie : « Apprendre une langue, c\'est ouvrir une nouvelle fenêtre sur le monde — donc je m\'y mets à fond. »',
  },
  {
    id: 'a2-m16', emoji: '🏃', title: 'Le sport et la santé',
    clues: [{ e: '⚽', t: 'sport' }, { e: '💪', t: 'santé' }, { e: '🕒', t: 'fréquence' }, { e: '😊', t: 'opinion' }],
    mission: 'Parle de l\'importance du sport.',
    nativeAlt: 'Au lieu de « Le sport est important », essaie : « Faire du sport régulièrement, ça me remet les idées en place et me donne un bon coup de fouet. »',
  },
  {
    id: 'a2-m17', emoji: '📺', title: 'Les écrans dans la vie quotidienne',
    clues: [{ e: '📱', t: 'utilisation' }, { e: '👨‍👩‍👧', t: 'famille' }, { e: '⚠️', t: 'problèmes' }, { e: '✅', t: 'équilibre' }],
    mission: 'Parle de l\'utilisation des écrans aujourd\'hui.',
    nativeAlt: 'Au lieu de « Les écrans sont mauvais », essaie : « Les écrans sont devenus omniprésents — c\'est pratique, cependant il faut savoir mettre des limites. »',
  },
  {
    id: 'a2-m18', emoji: '🐾', title: 'Les animaux domestiques',
    clues: [{ e: '🐶', t: 'avantages' }, { e: '🧹', t: 'responsabilités' }, { e: '❤️', t: 'émotions' }, { e: '🏠', t: 'maison' }],
    mission: 'Parle des animaux domestiques.',
    nativeAlt: 'Au lieu de « J\'aime les chiens », essaie : « Un chien, c\'est une vraie présence à la maison — ça demande des responsabilités, mais ça vaut le coup. »',
  },
  {
    id: 'a2-m19', emoji: '🌱', title: 'Protéger l\'environnement',
    clues: [{ e: '♻️', t: 'actions' }, { e: '🌍', t: 'pollution' }, { e: '🚲', t: 'habitudes' }, { e: '💡', t: 'solutions' }],
    mission: 'Explique comment protéger l\'environnement.',
    nativeAlt: 'Au lieu de « Il faut recycler », essaie : « Protéger la planète, ça commence par de petits gestes — trier ses déchets, consommer moins, puis pousser pour des solutions collectives. »',
  },
  {
    id: 'a2-m20', emoji: '🎯', title: 'Un objectif personnel',
    clues: [{ e: '💭', t: 'objectif' }, { e: '📅', t: 'futur' }, { e: '⚡', t: 'motivation' }, { e: '🚀', t: 'actions' }],
    mission: 'Parle d\'un objectif important pour toi.',
    nativeAlt: 'Au lieu de « Je veux apprendre le français », essaie : « Mon objectif, c\'est de parler français couramment — pour y arriver, je m\'entraîne un peu chaque jour, ensuite je m\'évalue à la fin du mois. »',
  },
];

/* -----------------------------------------------------------
   20 B1 speaking missions — opinion, narration, argumentation
   Each mission has a followUpType + followUpPrompt that powers
   the B1 "Continuer l'échange" extension (Opinion Booster /
   Debate / Storytelling).
----------------------------------------------------------- */
const MISSIONS_B1 = [
  {
    id: 'b1-m1', emoji: '📱', title: 'Les réseaux sociaux : utiles ou dangereux ?',
    context: 'Les réseaux sociaux occupent une place centrale dans la vie moderne.',
    clues: [{ e: '📲', t: 'avantages' }, { e: '⚠️', t: 'risques' }, { e: '👨‍👩‍👧', t: 'société' }, { e: '💭', t: 'opinion personnelle' }],
    mission: 'Donne ton opinion et justifie-la.',
    nativeAlt: 'Plutôt que « C\'est bien / C\'est mauvais », essaie : « À mon avis, les réseaux sociaux sont une formidable opportunité de connexion, cependant ils peuvent vite devenir addictifs. »',
    followUpType: 'debate', followUpPrompt: 'Certaines personnes pensent au contraire que les réseaux sociaux isolent plus qu\'ils ne connectent. Comment leur répondrais-tu ?',
  },
  {
    id: 'b1-m2', emoji: '🌍', title: 'Un voyage qui a changé ta façon de penser',
    context: 'Certaines expériences laissent une trace durable.',
    clues: [{ e: '✈️', t: 'destination' }, { e: '😲', t: 'découverte' }, { e: '🧠', t: 'changement' }, { e: '💡', t: 'leçon' }],
    mission: 'Raconte cette expérience et explique son impact.',
    nativeAlt: 'Plutôt que « C\'était cool », essaie : « Ce voyage m\'a profondément marqué — j\'en suis revenu(e) avec une vision plus large de ce qui compte vraiment. »',
    followUpType: 'story', followUpPrompt: 'Et ensuite ? Comment cette leçon influence-t-elle ta vie aujourd\'hui ?',
  },
  {
    id: 'b1-m3', emoji: '🎓', title: 'Faut-il apprendre plusieurs langues ?',
    context: 'Le multilinguisme est de plus en plus valorisé.',
    clues: [{ e: '🗣️', t: 'communication' }, { e: '💼', t: 'travail' }, { e: '🌍', t: 'culture' }, { e: '💭', t: 'opinion' }],
    mission: 'Défends ton point de vue avec des arguments précis.',
    nativeAlt: 'Plutôt que « Les langues sont utiles », essaie : « Apprendre une langue, c\'est élargir sa manière de penser — puisque chaque langue porte une vision du monde. »',
    followUpType: 'debate', followUpPrompt: 'On entend parfois que la traduction automatique rend l\'apprentissage des langues inutile. Que réponds-tu ?',
  },
  {
    id: 'b1-m4', emoji: '📚', title: 'Une personne qui t\'a inspiré(e)',
    context: 'Une rencontre ou un modèle marquant.',
    clues: [{ e: '👤', t: 'personne' }, { e: '⭐', t: 'qualités' }, { e: '📖', t: 'expérience' }, { e: '🎯', t: 'influence' }],
    mission: 'Explique pourquoi cette personne est importante.',
    nativeAlt: 'Plutôt que « Elle est gentille », essaie : « Cette personne incarne pour moi une certaine forme de courage — c\'est ce qui m\'a marqué le plus. »',
    followUpType: 'opinion', followUpPrompt: 'Pourquoi cette qualité te touche-t-elle particulièrement, toi ?',
  },
  {
    id: 'b1-m5', emoji: '🏙️', title: 'Ville ou campagne ?',
    context: 'Deux modes de vie, deux rythmes.',
    clues: [{ e: '🏢', t: 'ville' }, { e: '🌳', t: 'campagne' }, { e: '👍', t: 'avantages' }, { e: '👎', t: 'inconvénients' }],
    mission: 'Compare les deux modes de vie et exprime ta préférence.',
    nativeAlt: 'Plutôt que « La ville est mieux », essaie : « La ville offre une effervescence stimulante, en revanche la campagne apaise — d\'abord on respire, ensuite on prend le temps. »',
    followUpType: 'opinion', followUpPrompt: 'Et selon toi, qu\'est-ce qui pèse le plus dans ce choix : le confort matériel ou le bien-être mental ?',
  },
  {
    id: 'b1-m6', emoji: '🌱', title: 'Les gestes écologiques au quotidien',
    context: 'Chaque geste compte — mais sont-ils vraiment suffisants ?',
    clues: [{ e: '♻️', t: 'habitudes' }, { e: '🌍', t: 'environnement' }, { e: '⚡', t: 'difficultés' }, { e: '💡', t: 'solutions' }],
    mission: 'Explique ce que chacun peut faire et pourquoi.',
    nativeAlt: 'Plutôt que « Il faut recycler », essaie : « D\'abord, repenser ses petits gestes — trier, consommer moins. Ensuite, exiger des actions collectives plus ambitieuses. »',
    followUpType: 'debate', followUpPrompt: 'Certains disent que les gestes individuels sont une goutte d\'eau face aux grandes entreprises. Qu\'en penses-tu ?',
  },
  {
    id: 'b1-m7', emoji: '🤖', title: 'La technologie améliore-t-elle la vie ?',
    context: 'Outils miracles ou pièges modernes ?',
    clues: [{ e: '📱', t: 'innovation' }, { e: '⚠️', t: 'problèmes' }, { e: '👨‍👩‍👧', t: 'société' }, { e: '💭', t: 'opinion' }],
    mission: 'Présente un argumentaire équilibré.',
    nativeAlt: 'Plutôt que « C\'est utile et dangereux », essaie : « La technologie nous facilite la vie sur bien des plans, pourtant elle crée aussi de nouvelles formes de fatigue mentale. »',
    followUpType: 'debate', followUpPrompt: 'On dit parfois que la génération hyperconnectée est moins heureuse. Es-tu d\'accord ?',
  },
  {
    id: 'b1-m8', emoji: '💼', title: 'Le métier de tes rêves',
    context: 'Un projet professionnel qui te ressemble.',
    clues: [{ e: '🎯', t: 'profession' }, { e: '😊', t: 'motivation' }, { e: '📈', t: 'avenir' }, { e: '⚡', t: 'défis' }],
    mission: 'Explique ton choix et ce que tu mets en place pour y arriver.',
    nativeAlt: 'Plutôt que « Je veux devenir architecte », essaie : « Ce qui m\'attire dans ce métier, c\'est cette possibilité de transformer un espace, donc d\'agir sur la vie des gens. »',
    followUpType: 'opinion', followUpPrompt: 'Pourquoi ce métier en particulier, et pas un autre proche ?',
  },
  {
    id: 'b1-m9', emoji: '📺', title: 'Les influenceurs ont-ils trop de pouvoir ?',
    context: 'Une nouvelle forme d\'autorité culturelle.',
    clues: [{ e: '📱', t: 'réseaux sociaux' }, { e: '👥', t: 'jeunes' }, { e: '⭐', t: 'influence' }, { e: '💭', t: 'opinion' }],
    mission: 'Développe ton point de vue avec nuance.',
    nativeAlt: 'Plutôt que « Ils ont trop de pouvoir », essaie : « Les influenceurs occupent un rôle culturel énorme — en revanche, ce pouvoir n\'est encadré par presque aucune responsabilité. »',
    followUpType: 'debate', followUpPrompt: 'Certains pensent que les influenceurs sont juste les nouveaux artistes — qu\'en penses-tu ?',
  },
  {
    id: 'b1-m10', emoji: '🎬', title: 'Une histoire surprenante que tu as vécue',
    context: 'Un moment où la réalité a dépassé l\'imagination.',
    clues: [{ e: '📍', t: 'contexte' }, { e: '😲', t: 'événement' }, { e: '🗣️', t: 'réaction' }, { e: '🎯', t: 'conclusion' }],
    mission: 'Raconte cette histoire comme un narrateur — d\'abord la scène, ensuite la surprise, finalement la chute.',
    nativeAlt: 'Plutôt que « C\'était bizarre », essaie : « Sur le moment, je n\'arrivais pas à y croire — c\'était presque irréel. »',
    followUpType: 'story', followUpPrompt: 'Et ensuite ? Comment cette histoire a-t-elle changé ton regard sur la situation ?',
  },
  {
    id: 'b1-m11', emoji: '📖', title: 'Le livre ou le film qui t\'a marqué(e)',
    context: 'Une œuvre qui continue à résonner en toi.',
    clues: [{ e: '📚', t: 'œuvre' }, { e: '🎭', t: 'thème' }, { e: '❤️', t: 'émotion' }, { e: '💡', t: 'message' }],
    mission: 'Présente ton analyse personnelle.',
    nativeAlt: 'Plutôt que « C\'est une bonne histoire », essaie : « Ce qui m\'a bouleversé(e) dans cette œuvre, c\'est la manière dont elle aborde la solitude — finement, sans pathos. »',
    followUpType: 'opinion', followUpPrompt: 'Pourquoi ce message en particulier t\'a-t-il touché(e), à ce moment de ta vie ?',
  },
  {
    id: 'b1-m12', emoji: '🚗', title: 'Les transports publics sont-ils suffisants ?',
    context: 'Mobilité, écologie et accès à la ville.',
    clues: [{ e: '🚇', t: 'avantages' }, { e: '⚠️', t: 'problèmes' }, { e: '🌍', t: 'impact écologique' }, { e: '💭', t: 'solutions' }],
    mission: 'Exprime ton opinion en t\'appuyant sur ton expérience.',
    nativeAlt: 'Plutôt que « Le bus n\'est pas bien », essaie : « Le réseau actuel reste insuffisant, surtout en périphérie — pourtant, c\'est précisément là qu\'on en aurait le plus besoin. »',
    followUpType: 'debate', followUpPrompt: 'On nous dit que la voiture individuelle restera dominante. Es-tu d\'accord ?',
  },
  {
    id: 'b1-m13', emoji: '🧒', title: 'L\'enfance aujourd\'hui est-elle différente ?',
    context: 'Une génération sur écran, une autre dans les parcs.',
    clues: [{ e: '🎮', t: 'loisirs' }, { e: '📱', t: 'technologie' }, { e: '🏫', t: 'école' }, { e: '💭', t: 'comparaison' }],
    mission: 'Compare différentes générations avec nuance.',
    nativeAlt: 'Plutôt que « Avant c\'était mieux », essaie : « Les enfants d\'aujourd\'hui grandissent dans un monde infiniment plus connecté, en revanche, ils perdent peut-être un certain rapport au temps long. »',
    followUpType: 'opinion', followUpPrompt: 'Selon toi, quel est le plus grand atout — et la plus grande perte — de cette génération ?',
  },
  {
    id: 'b1-m14', emoji: '🎯', title: 'Une décision difficile que tu as prise',
    context: 'Un croisement de vie où il a fallu choisir.',
    clues: [{ e: '🤔', t: 'situation' }, { e: '⚡', t: 'choix' }, { e: '😟', t: 'difficulté' }, { e: '🎉', t: 'résultat' }],
    mission: 'Raconte et analyse cette décision avec recul.',
    nativeAlt: 'Plutôt que « C\'était dur », essaie : « Sur le moment, j\'étais déchiré(e) — pourtant, avec le recul, c\'est probablement la meilleure décision que j\'ai prise. »',
    followUpType: 'story', followUpPrompt: 'Et ensuite ? Que dirais-tu à quelqu\'un qui se trouverait dans la même situation aujourd\'hui ?',
  },
  {
    id: 'b1-m15', emoji: '🏆', title: 'Qu\'est-ce que le succès ?',
    context: 'Argent, reconnaissance, sérénité — où placer le curseur ?',
    clues: [{ e: '💰', t: 'argent' }, { e: '😊', t: 'bonheur' }, { e: '🎯', t: 'objectifs' }, { e: '💭', t: 'définition personnelle' }],
    mission: 'Développe ta propre vision du succès.',
    nativeAlt: 'Plutôt que « Le succès c\'est gagner de l\'argent », essaie : « Pour moi, réussir, c\'est trouver un équilibre entre ce que je fais et ce que je suis — donc, ce n\'est pas un sommet mais un chemin. »',
    followUpType: 'opinion', followUpPrompt: 'Pourquoi cette définition-là, et pas celle qu\'on entend partout ?',
  },
  {
    id: 'b1-m16', emoji: '🌎', title: 'Les voyages forment-ils vraiment la jeunesse ?',
    context: 'Un dicton célèbre — toujours valable ?',
    clues: [{ e: '✈️', t: 'découverte' }, { e: '🌍', t: 'culture' }, { e: '🧠', t: 'apprentissage' }, { e: '💭', t: 'opinion' }],
    mission: 'Argumente avec exemples concrets.',
    nativeAlt: 'Plutôt que « Les voyages c\'est bien », essaie : « Voyager apprend ce qu\'aucun livre ne raconte vraiment — d\'abord à se décentrer, ensuite à relativiser. »',
    followUpType: 'debate', followUpPrompt: 'On dit aussi qu\'on peut tout aussi bien grandir sans voyager. Qu\'en penses-tu ?',
  },
  {
    id: 'b1-m17', emoji: '🎭', title: 'Faut-il toujours dire la vérité ?',
    context: 'Honnêteté, tact, et zones grises.',
    clues: [{ e: '🤝', t: 'honnêteté' }, { e: '😬', t: 'situations difficiles' }, { e: '❤️', t: 'relations' }, { e: '💭', t: 'opinion' }],
    mission: 'Développe un raisonnement nuancé, avec des exemples.',
    nativeAlt: 'Plutôt que « Il faut dire la vérité », essaie : « La vérité reste pour moi une valeur fondamentale — cependant, la dire sans tact peut faire plus de mal que de bien. »',
    followUpType: 'debate', followUpPrompt: 'Y a-t-il des situations où mentir est, selon toi, justifié ?',
  },
  {
    id: 'b1-m18', emoji: '🏫', title: 'Les devoirs à la maison sont-ils utiles ?',
    context: 'Un débat éducatif qui n\'en finit pas.',
    clues: [{ e: '📚', t: 'apprentissage' }, { e: '⏰', t: 'temps' }, { e: '👨‍🎓', t: 'résultats' }, { e: '💭', t: 'opinion' }],
    mission: 'Présente des arguments pour et contre, puis conclus.',
    nativeAlt: 'Plutôt que « Je n\'aime pas les devoirs », essaie : « Les devoirs renforcent les apprentissages, pourtant ils empiètent largement sur la vie familiale — donc la question est celle du dosage. »',
    followUpType: 'debate', followUpPrompt: 'Les pays sans devoirs obtiennent parfois de meilleurs résultats. Comment l\'expliques-tu ?',
  },
  {
    id: 'b1-m19', emoji: '🌐', title: 'L\'intelligence artificielle dans notre quotidien',
    context: 'Outil libérateur ou menace silencieuse ?',
    clues: [{ e: '🤖', t: 'usages' }, { e: '👍', t: 'avantages' }, { e: '⚠️', t: 'risques' }, { e: '🔮', t: 'futur' }],
    mission: 'Analyse son impact sur la société.',
    nativeAlt: 'Plutôt que « L\'IA va tout changer », essaie : « L\'IA bouleverse déjà nos manières de travailler — selon moi, le vrai défi est d\'en garder le contrôle collectif. »',
    followUpType: 'debate', followUpPrompt: 'Certains pensent que l\'IA va remplacer beaucoup de métiers. Comment réagis-tu à cela ?',
  },
  {
    id: 'b1-m20', emoji: '🎤', title: 'Si tu pouvais changer une chose dans le monde',
    context: 'Une intuition, une cause, une vision.',
    clues: [{ e: '🌍', t: 'problème' }, { e: '💡', t: 'solution' }, { e: '👥', t: 'impact' }, { e: '🎯', t: 'objectif' }],
    mission: 'Présente une idée convaincante et bien structurée.',
    nativeAlt: 'Plutôt que « Je voudrais la paix », essaie : « S\'il y a une chose que je changerais, ce serait notre rapport à la lenteur — finalement, beaucoup de problèmes viennent de notre obsession de la vitesse. »',
    followUpType: 'story', followUpPrompt: 'Et ensuite ? Imagine concrètement la première étape pour y arriver.',
  },
];

/* -----------------------------------------------------------
   20 B2 speaking missions — abstract / societal / ethical
   followUpType: 'counter' (Counterargument), 'nuance' (Nuance),
   or 'reflection' (Deep Reflection — long-term consequences)
----------------------------------------------------------- */
const MISSIONS_B2 = [
  {
    id: 'b2-m1', emoji: '🤖', title: 'L\'intelligence artificielle remplacera-t-elle certains métiers ?',
    context: 'L\'IA transforme rapidement le monde du travail.',
    clues: [{ e: '🤖', t: 'innovation' }, { e: '💼', t: 'emploi' }, { e: '⚖️', t: 'avantages / risques' }, { e: '🔮', t: 'futur' }],
    mission: 'Analyse les conséquences possibles et défends ton point de vue.',
    nativeAlt: 'Plutôt que « L\'IA va remplacer les humains », essaie : « À mon sens, l\'IA ne remplacera pas les métiers en bloc — elle les recomposera, ce qui exige néanmoins une véritable politique de formation. »',
    followUpType: 'reflection', followUpPrompt: 'Quelles pourraient être les conséquences à long terme pour la cohésion sociale, et comment s\'y préparer dès aujourd\'hui ?',
  },
  {
    id: 'b2-m2', emoji: '🌍', title: 'La mondialisation : opportunité ou menace ?',
    context: 'Un phénomène à plusieurs visages.',
    clues: [{ e: '🌎', t: 'économie' }, { e: '🏛️', t: 'culture' }, { e: '💼', t: 'entreprises' }, { e: '⚖️', t: 'équilibre' }],
    mission: 'Présente une analyse nuancée des bénéfices et des coûts.',
    nativeAlt: 'Plutôt que « C\'est bien et mal », essaie : « D\'une part, la mondialisation a sorti des millions de personnes de la pauvreté ; d\'autre part, elle a creusé d\'autres formes d\'inégalités. »',
    followUpType: 'counter', followUpPrompt: 'Certains soutiennent qu\'il faudrait au contraire ralentir, voire inverser la mondialisation. Comment réponds-tu à cette position ?',
  },
  {
    id: 'b2-m3', emoji: '📱', title: 'Les réseaux sociaux favorisent-ils réellement la communication ?',
    context: 'Connexion permanente ou solitude amplifiée ?',
    clues: [{ e: '📲', t: 'connexion' }, { e: '😕', t: 'isolement' }, { e: '👥', t: 'société' }, { e: '💭', t: 'opinion' }],
    mission: 'Discute des effets positifs et négatifs avec exemples.',
    nativeAlt: 'Plutôt que « Ils relient et isolent », essaie : « Les réseaux sociaux ont indéniablement étendu nos cercles, toutefois la qualité des échanges s\'est, dans une certaine mesure, appauvrie. »',
    followUpType: 'counter', followUpPrompt: 'Beaucoup affirment que les réseaux ne sont qu\'un miroir de nos propres comportements. Que leur répondrais-tu ?',
  },
  {
    id: 'b2-m4', emoji: '🎓', title: 'Les diplômes sont-ils encore indispensables ?',
    context: 'À l\'heure des compétences autodidactes et de l\'auto-apprentissage.',
    clues: [{ e: '📚', t: 'éducation' }, { e: '💼', t: 'emploi' }, { e: '⭐', t: 'compétences' }, { e: '🔮', t: 'évolution' }],
    mission: 'Développe un argumentaire structuré.',
    nativeAlt: 'Plutôt que « Les diplômes sont obsolètes », essaie : « Il convient de reconnaître que le diplôme reste un signal social fort, bien que la valeur des compétences pratiques gagne du terrain. »',
    followUpType: 'nuance', followUpPrompt: 'Peux-tu présenter un argument qui irait à l\'encontre de ta position, et expliquer pourquoi il mérite d\'être pris au sérieux ?',
  },
  {
    id: 'b2-m5', emoji: '🌱', title: 'Peut-on concilier croissance économique et protection de l\'environnement ?',
    context: 'Le grand dilemme de notre siècle.',
    clues: [{ e: '💰', t: 'économie' }, { e: '🌍', t: 'écologie' }, { e: '⚡', t: 'défis' }, { e: '💡', t: 'solutions' }],
    mission: 'Analyse les tensions et les compromis possibles.',
    nativeAlt: 'Plutôt que « C\'est impossible », essaie : « Force est de constater qu\'il existe une tension structurelle entre ces deux objectifs — la question est de savoir si l\'innovation peut les rendre compatibles à long terme. »',
    followUpType: 'reflection', followUpPrompt: 'Quelles pourraient être les conséquences à long terme si nous échouons à concilier ces deux impératifs ?',
  },
  {
    id: 'b2-m6', emoji: '🏙️', title: 'Les villes de demain',
    context: 'Réinventer l\'urbain pour le siècle qui vient.',
    clues: [{ e: '🚇', t: 'mobilité' }, { e: '🌳', t: 'durabilité' }, { e: '🏢', t: 'urbanisme' }, { e: '👥', t: 'qualité de vie' }],
    mission: 'Imagine et décris une ville idéale du futur, avec une vision cohérente.',
    nativeAlt: 'Plutôt que « Une ville verte et propre », essaie : « Dans l\'hypothèse où l\'on conçoit la ville comme un écosystème vivant, il serait pertinent de repenser entièrement nos rapports à la mobilité, à l\'air et au silence. »',
    followUpType: 'reflection', followUpPrompt: 'À long terme, quelle est la première transformation urbaine que nous devrions absolument réussir, et pourquoi ?',
  },
  {
    id: 'b2-m7', emoji: '🎭', title: 'Faut-il toujours séparer l\'artiste de son œuvre ?',
    context: 'Une question morale qui revient à chaque scandale.',
    clues: [{ e: '🎨', t: 'création' }, { e: '⚖️', t: 'morale' }, { e: '👥', t: 'société' }, { e: '💭', t: 'opinion' }],
    mission: 'Présente plusieurs points de vue avant de conclure.',
    nativeAlt: 'Plutôt que « Ça dépend », essaie : « Il me semble que la séparation absolue est intenable, quoiqu\'il faille éviter de réduire une œuvre à son auteur. »',
    followUpType: 'nuance', followUpPrompt: 'Peux-tu défendre la position opposée à la tienne, comme le ferait un philosophe que tu respectes ?',
  },
  {
    id: 'b2-m8', emoji: '💼', title: 'Le télétravail est-il une révolution durable ?',
    context: 'Une transformation accélérée par la crise sanitaire.',
    clues: [{ e: '🏠', t: 'travail' }, { e: '😊', t: 'avantages' }, { e: '⚠️', t: 'limites' }, { e: '🔮', t: 'avenir' }],
    mission: 'Évalue son impact sur la société et l\'organisation du travail.',
    nativeAlt: 'Plutôt que « Le télétravail est utile », essaie : « Le télétravail a réinventé notre rapport au temps et à l\'espace, néanmoins il fragilise certains liens collectifs qu\'il convient de préserver. »',
    followUpType: 'reflection', followUpPrompt: 'À long terme, quels seront les effets du télétravail sur la ville, la solitude et la culture d\'entreprise ?',
  },
  {
    id: 'b2-m9', emoji: '🧠', title: 'L\'échec est-il nécessaire à la réussite ?',
    context: 'Une formule à la mode — mais est-elle vraie ?',
    clues: [{ e: '📉', t: 'échec' }, { e: '📈', t: 'réussite' }, { e: '💪', t: 'apprentissage' }, { e: '🎯', t: 'expérience' }],
    mission: 'Développe une réflexion personnelle argumentée.',
    nativeAlt: 'Plutôt que « L\'échec rend plus fort », essaie : « Bien que l\'échec puisse être formateur, sa célébration systématique me semble parfois dissimuler des privilèges qui permettent justement de rebondir. »',
    followUpType: 'nuance', followUpPrompt: 'Présente un contre-argument convaincant à ta position, comme si tu changeais de rôle dans un débat.',
  },
  {
    id: 'b2-m10', emoji: '🌐', title: 'Les langues minoritaires doivent-elles être davantage protégées ?',
    context: 'Patrimoine vivant ou frein à l\'universalité ?',
    clues: [{ e: '🗣️', t: 'diversité' }, { e: '🏛️', t: 'patrimoine' }, { e: '🌍', t: 'mondialisation' }, { e: '💡', t: 'solutions' }],
    mission: 'Défends une position avec des exemples concrets.',
    nativeAlt: 'Plutôt que « Il faut les protéger », essaie : « Chaque langue qui disparaît emporte une vision unique du monde, c\'est pourquoi il serait pertinent de soutenir activement leur transmission. »',
    followUpType: 'counter', followUpPrompt: 'On entend que protéger ces langues serait un combat d\'arrière-garde. Comment réponds-tu à cet argument ?',
  },
  {
    id: 'b2-m11', emoji: '📺', title: 'Les médias influencent-ils trop l\'opinion publique ?',
    context: 'Quatrième pouvoir ou simple miroir ?',
    clues: [{ e: '📰', t: 'information' }, { e: '📢', t: 'influence' }, { e: '⚖️', t: 'responsabilité' }, { e: '👥', t: 'citoyens' }],
    mission: 'Analyse le rôle des médias aujourd\'hui.',
    nativeAlt: 'Plutôt que « Les médias manipulent », essaie : « Les médias façonnent indéniablement l\'agenda public, toutefois leur influence dépend largement de la capacité critique des citoyens. »',
    followUpType: 'counter', followUpPrompt: 'Certains affirment que ce sont les algorithmes — non les médias traditionnels — qui dictent désormais l\'opinion. Que réponds-tu ?',
  },
  {
    id: 'b2-m12', emoji: '🚀', title: 'Le tourisme spatial est-il une priorité ?',
    context: 'Frontière ultime ou caprice de milliardaires ?',
    clues: [{ e: '🚀', t: 'innovation' }, { e: '💰', t: 'coût' }, { e: '🌍', t: 'priorités mondiales' }, { e: '⚖️', t: 'éthique' }],
    mission: 'Présente un débat équilibré.',
    nativeAlt: 'Plutôt que « C\'est inutile », essaie : « Le tourisme spatial peut sembler frivole face aux urgences terrestres, néanmoins il pousse aussi à des innovations dont nous bénéficierons à long terme. »',
    followUpType: 'counter', followUpPrompt: 'On entend que cet argent serait mieux investi dans la lutte contre la pauvreté. Es-tu d\'accord ?',
  },
  {
    id: 'b2-m13', emoji: '🎯', title: 'Le bonheur dépend-il principalement de nous ?',
    context: 'Question millénaire, débats toujours actuels.',
    clues: [{ e: '😊', t: 'bonheur' }, { e: '👨‍👩‍👧', t: 'entourage' }, { e: '💰', t: 'conditions de vie' }, { e: '🧠', t: 'psychologie' }],
    mission: 'Développe une réflexion nuancée.',
    nativeAlt: 'Plutôt que « Oui / non », essaie : « À mon sens, le bonheur relève en partie de notre intériorité, bien que les conditions matérielles et sociales restent déterminantes. »',
    followUpType: 'nuance', followUpPrompt: 'Présente un argument qui s\'oppose à ta position et explique pourquoi il mérite considération.',
  },
  {
    id: 'b2-m14', emoji: '📚', title: 'Faut-il limiter l\'usage des écrans chez les jeunes ?',
    context: 'Un débat public majeur dans toutes les démocraties.',
    clues: [{ e: '📱', t: 'technologie' }, { e: '👶', t: 'développement' }, { e: '🏫', t: 'éducation' }, { e: '⚖️', t: 'équilibre' }],
    mission: 'Présente arguments et contre-arguments avant de conclure.',
    nativeAlt: 'Plutôt que « Il faut interdire », essaie : « Il convient de distinguer l\'usage scolaire de l\'usage récréatif — limiter le second me semble nécessaire, quoique difficile à mettre en pratique. »',
    followUpType: 'counter', followUpPrompt: 'Certaines personnes soutiennent qu\'apprendre à gérer les écrans est plus utile que de les limiter. Que penses-tu de cette position ?',
  },
  {
    id: 'b2-m15', emoji: '🌍', title: 'Les citoyens ont-ils un réel pouvoir face aux grandes entreprises ?',
    context: 'David contre Goliath, version XXIᵉ siècle.',
    clues: [{ e: '🏢', t: 'multinationales' }, { e: '👥', t: 'consommateurs' }, { e: '📢', t: 'mobilisation' }, { e: '⚖️', t: 'influence' }],
    mission: 'Analyse les rapports de force avec exemples.',
    nativeAlt: 'Plutôt que « Non, on ne peut rien faire », essaie : « Le pouvoir citoyen reste limité face aux multinationales, néanmoins certains boycotts récents montrent qu\'il peut, par moments, peser de manière décisive. »',
    followUpType: 'reflection', followUpPrompt: 'À long terme, quels leviers démocratiques pourraient rééquilibrer ce rapport de force ?',
  },
  {
    id: 'b2-m16', emoji: '🤝', title: 'Peut-on tout pardonner ?',
    context: 'Une question intime et collective.',
    clues: [{ e: '❤️', t: 'pardon' }, { e: '⚖️', t: 'justice' }, { e: '🧠', t: 'psychologie' }, { e: '👥', t: 'relations humaines' }],
    mission: 'Explore différentes perspectives avec sensibilité.',
    nativeAlt: 'Plutôt que « Non, certaines choses sont impardonnables », essaie : « Il me semble que le pardon est avant tout un acte intérieur, quoiqu\'il ne puisse jamais effacer la nécessité de la justice. »',
    followUpType: 'nuance', followUpPrompt: 'Peux-tu défendre la position contraire à la tienne, comme le ferait quelqu\'un qui aurait vécu une expérience opposée ?',
  },
  {
    id: 'b2-m17', emoji: '🧬', title: 'Les progrès scientifiques doivent-ils avoir des limites ?',
    context: 'Génétique, IA, neurosciences : jusqu\'où aller ?',
    clues: [{ e: '🔬', t: 'recherche' }, { e: '⚖️', t: 'éthique' }, { e: '🚀', t: 'innovation' }, { e: '👥', t: 'société' }],
    mission: 'Discute des enjeux éthiques avec nuance.',
    nativeAlt: 'Plutôt que « Il faut des limites », essaie : « Force est de constater qu\'aucune limite ne tient longtemps face à la curiosité humaine — il serait donc plus pertinent d\'encadrer démocratiquement la science que de prétendre l\'arrêter. »',
    followUpType: 'reflection', followUpPrompt: 'Quelles pourraient être les conséquences à long terme d\'une absence totale de régulation ?',
  },
  {
    id: 'b2-m18', emoji: '🎓', title: 'Apprendre toute sa vie : nécessité ou choix ?',
    context: 'Le mythe de la formation continue.',
    clues: [{ e: '📚', t: 'formation' }, { e: '💼', t: 'carrière' }, { e: '🧠', t: 'développement personnel' }, { e: '🔮', t: 'avenir' }],
    mission: 'Développe un raisonnement structuré.',
    nativeAlt: 'Plutôt que « Oui c\'est important », essaie : « Dans une certaine mesure, l\'apprentissage permanent est devenu une exigence — toutefois, en faire une obligation morale peut se révéler oppressant. »',
    followUpType: 'nuance', followUpPrompt: 'Peux-tu présenter un argument contraire au tien — celui d\'une personne qui valorise au contraire la lenteur et la stabilité ?',
  },
  {
    id: 'b2-m19', emoji: '🌎', title: 'Les crises mondiales renforcent-elles la solidarité ?',
    context: 'Pandémies, guerres, climat — qu\'avons-nous vraiment appris ?',
    clues: [{ e: '🤝', t: 'coopération' }, { e: '🌍', t: 'crises' }, { e: '🏛️', t: 'institutions' }, { e: '💭', t: 'opinion' }],
    mission: 'Analyse cette affirmation avec exemples historiques récents.',
    nativeAlt: 'Plutôt que « Oui, en théorie », essaie : « Les grandes crises révèlent souvent le meilleur et le pire de nos sociétés — bien que la solidarité émerge initialement, elle s\'érode rapidement dès que la peur s\'estompe. »',
    followUpType: 'counter', followUpPrompt: 'On entend aussi que les crises divisent plus qu\'elles ne rassemblent. Comment articules-tu ces deux constats ?',
  },
  {
    id: 'b2-m20', emoji: '🎤', title: 'Quelle valeur devrait être la plus importante dans une société moderne ?',
    context: 'Justice, solidarité, liberté, responsabilité — où placer la priorité ?',
    clues: [{ e: '⚖️', t: 'justice' }, { e: '🤝', t: 'solidarité' }, { e: '🗽', t: 'liberté' }, { e: '🌍', t: 'responsabilité' }],
    mission: 'Développe une réflexion argumentée et personnelle.',
    nativeAlt: 'Plutôt que « La liberté est importante », essaie : « Il me semble que la justice est le socle sur lequel les autres valeurs peuvent exister — sans elle, la liberté devient privilège et la solidarité, charité. »',
    followUpType: 'nuance', followUpPrompt: 'Peux-tu présenter la valeur opposée comme prioritaire — et expliquer en quoi cet argument est défendable ?',
  },
];

const B2_CONNECTORS = ['à mon sens', 'il convient de', 'il me semble que', 'toutefois', 'néanmoins', 'en revanche', 'd\'une part', 'd\'autre part', 'force est de constater que', 'il serait pertinent de', 'bien que', 'quoique', 'dans l\'hypothèse où', 'à long terme', 'dans une certaine mesure'];

const MISSIONS_BY_LEVEL = { A1: MISSIONS_A1, A2: MISSIONS_A2, B1: MISSIONS_B1, B2: MISSIONS_B2 };
const LEVELS = ['A1', 'A2', 'B1', 'B2'];

const A2_CONNECTORS = ['parce que', 'mais', 'puis', 'ensuite', 'cependant', 'donc'];
const B1_CONNECTORS = ['parce que', 'puisque', 'cependant', 'pourtant', 'donc', 'en revanche', 'd\'abord', 'ensuite', 'finalement', 'à mon avis', 'selon moi'];

const CONNECTORS_BY_LEVEL = { A1: null, A2: A2_CONNECTORS, B1: B1_CONNECTORS, B2: B2_CONNECTORS };

const FOLLOWUP_LABELS = {
  opinion: { eyebrow: 'Opinion Booster', cta: 'Approfondir mon opinion' },
  debate: { eyebrow: 'Débat', cta: 'Répondre au contre-argument' },
  story: { eyebrow: 'Suite de l\'histoire', cta: 'Continuer mon récit' },
  counter: { eyebrow: 'Counterargument Challenge', cta: 'Répondre à l\'opinion inverse' },
  nuance: { eyebrow: 'Nuance Challenge', cta: 'Défendre la position opposée' },
  reflection: { eyebrow: 'Deep Reflection', cta: 'Analyser les conséquences à long terme' },
};

const TIMER_MODES_BY_LEVEL = {
  A1: [
    { id: 'easy', name: 'Découverte', seconds: 20, sub: '20 sec' },
    { id: 'normal', name: 'Normal', seconds: 30, sub: '30 sec' },
    { id: 'challenge', name: 'Défi', seconds: 45, sub: '45 sec' },
  ],
  A2: [
    { id: 'standard', name: 'Standard', seconds: 45, sub: '45 sec' },
    { id: 'challenge', name: 'Challenge', seconds: 60, sub: '60 sec' },
    { id: 'expert', name: 'Expert', seconds: 75, sub: '75 sec' },
  ],
  B1: [
    { id: 'standard', name: 'Standard', seconds: 60, sub: '60 sec' },
    { id: 'challenge', name: 'Challenge', seconds: 75, sub: '75 sec' },
    { id: 'expert', name: 'Expert', seconds: 90, sub: '90 sec' },
  ],
  B2: [
    { id: 'standard', name: 'Standard', seconds: 90, sub: '90 sec' },
    { id: 'challenge', name: 'Challenge', seconds: 105, sub: '105 sec' },
    { id: 'expert', name: 'Expert', seconds: 120, sub: '120 sec' },
  ],
};

/* -----------------------------------------------------------
   Streak (localStorage)
----------------------------------------------------------- */
const STREAK_KEY = 'selora.parle.streak';

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const loadStreak = () => {
  try {
    const raw = window.localStorage.getItem(STREAK_KEY);
    if (!raw) return { dates: [], count: 0 };
    const parsed = JSON.parse(raw);
    return { dates: parsed.dates || [], count: parsed.count || 0 };
  } catch {
    return { dates: [], count: 0 };
  }
};

const bumpStreak = () => {
  const today = todayKey();
  const { dates } = loadStreak();
  if (dates.includes(today)) return loadStreak();
  // compute streak run length ending today
  const all = [...dates, today].sort();
  let run = 1;
  for (let i = all.length - 2; i >= 0; i--) {
    const cur = new Date(all[i + 1]);
    const prev = new Date(all[i]);
    const diff = Math.round((cur - prev) / (1000 * 60 * 60 * 24));
    if (diff === 1) run += 1;
    else break;
  }
  const next = { dates: all, count: run };
  try { window.localStorage.setItem(STREAK_KEY, JSON.stringify(next)); } catch {}
  return next;
};

/* -----------------------------------------------------------
   Simulated AI feedback (pronun / fluency / vocab / grammar)
----------------------------------------------------------- */
function makeFeedback(level = 'A1') {
  const base = 70 + Math.floor(Math.random() * 22);
  const pronun = Math.min(98, Math.max(65, base + (Math.floor(Math.random() * 8) - 4)));
  const fluid = Math.min(98, Math.max(60, base + (Math.floor(Math.random() * 10) - 5)));
  const vocab = Math.min(98, Math.max(60, base + (Math.floor(Math.random() * 10) - 5)));
  const grammar = Math.min(98, Math.max(60, base + (Math.floor(Math.random() * 10) - 5)));
  const strengths = [];
  const tips = [];
  if (pronun > 80) strengths.push('Belle clarté de prononciation.');
  if (fluid > 78) strengths.push('Débit naturel et fluide.');
  if (vocab > 78) strengths.push(`Vocabulaire varié pour le niveau ${level}.`);
  if (grammar > 78) {
    if (level === 'B2') strengths.push('Raisonnement nuancé, niveau de langue soigné.');
    else if (level === 'B1') strengths.push('Argumentation structurée et idées articulées.');
    else if (level === 'A2') strengths.push('Phrases connectées et bien organisées.');
    else strengths.push('Structures simples bien maîtrisées.');
  }
  if (pronun < 80) tips.push('Travaille la prononciation des voyelles nasales (an, on, in).');
  if (fluid < 78) tips.push(level === 'B1' || level === 'B2'
    ? 'Réduis les hésitations sur les transitions — un connecteur clair (donc, cependant, néanmoins) suffit souvent à reprendre le souffle.'
    : 'Essaie d\'enchaîner les idées sans pause longue.');
  if (vocab < 78) {
    if (level === 'B2') tips.push('Élève le registre : remplace « beaucoup » par « considérablement », « important » par « décisif », « bien » par « pertinent ».');
    else if (level === 'B1') tips.push('Évite les répétitions et varie tes adjectifs (intéressant → marquant, captivant, stimulant…).');
    else if (level === 'A2') tips.push('Varie les adjectifs et évite les répétitions (sympa, intéressant, agréable…).');
    else tips.push('Ajoute un adjectif descriptif (sympa, calme, bruyant…).');
  }
  if (grammar < 78) {
    if (level === 'B2') tips.push('Introduis de la nuance avec « bien que », « quoique », « dans une certaine mesure » — et anticipe le contre-argument avant de le réfuter.');
    else if (level === 'B1') tips.push('Structure ton argumentation : d\'abord, ensuite, finalement — et donne un avis nuancé.');
    else if (level === 'A2') tips.push('Connecte tes idées : parce que, mais, puis, ensuite, cependant, donc.');
    else tips.push('Pense à la concordance simple : je + verbe en -e.');
  }
  if (!strengths.length) strengths.push('Bel effort — chaque tentative compte.');
  if (!tips.length) tips.push('Continue à varier les expressions naturelles.');
  return { pronun, fluid, vocab, grammar, strengths, tips };
}

/* -----------------------------------------------------------
   Recorder hook
----------------------------------------------------------- */
function useRecorder() {
  const [recording, setRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mrRef.current = mr;
      mr.start();
      setRecording(true);
      return true;
    } catch {
      alert('Microphone non disponible — vérifie les permissions de ton navigateur.');
      return false;
    }
  }, []);

  const stop = useCallback(() => {
    if (mrRef.current && recording) {
      mrRef.current.stop();
      setRecording(false);
    }
  }, [recording]);

  const reset = useCallback(() => {
    setAudioUrl(null);
    setAudioBlob(null);
    chunksRef.current = [];
  }, []);

  return { recording, audioUrl, audioBlob, start, stop, reset };
}

/* -----------------------------------------------------------
   Mission card
----------------------------------------------------------- */
function MissionCard({ mission, timerSeconds, onComplete, level = 'A1', randomClueMode = false }) {
  const { recording, audioUrl, audioBlob, start, stop, reset } = useRecorder();
  const [remaining, setRemaining] = useState(timerSeconds);
  const [phase, setPhase] = useState('idle'); // idle | recording | done
  const [feedback, setFeedback] = useState(null);
  const [revealed, setRevealed] = useState(randomClueMode ? 1 : mission.clues.length);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const tickRef = useRef(null);

  // Reset everything when mission, level, timer, or reveal-mode changes
  useEffect(() => {
    setPhase('idle');
    setRemaining(timerSeconds);
    setFeedback(null);
    setRevealed(randomClueMode ? 1 : mission.clues.length);
    setSubmitted(false);
    setSubmitError('');
    reset();
    if (tickRef.current) clearInterval(tickRef.current);
    if (recording) stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mission.id, timerSeconds, randomClueMode]);

  const handleStart = async () => {
    setFeedback(null);
    reset();
    setRemaining(timerSeconds);
    const ok = await start();
    if (!ok) return; // mic denied — stay in idle
    setPhase('recording');
    tickRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(tickRef.current);
          stop();
          setPhase('done');
          const fb = makeFeedback(level);
          setFeedback(fb);
          onComplete?.();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
  };

  const handleStopEarly = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    stop();
    setPhase('done');
    const fb = makeFeedback(level);
    setFeedback(fb);
    onComplete?.();
  };

  const handleReset = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    setPhase('idle');
    setRemaining(timerSeconds);
    setFeedback(null);
    setSubmitted(false);
    setSubmitError('');
    reset();
  };

  const handleSubmit = async () => {
    if (!audioBlob) {
      setSubmitError("Aucun enregistrement à envoyer.");
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      await uploadRecording(audioBlob, {
        level,
        exerciseId: mission.id || `${level}-parle-indices`.toLowerCase(),
        exerciseTitle: `${mission.title || 'Parle avec des indices'} — Parle avec des indices`,
        promptId: String(mission.id || ''),
        promptText: mission.mission || mission.title || '',
        durationSeconds: timerSeconds - remaining,
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Envoi impossible. Réessaie dans un instant.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => () => { if (tickRef.current) clearInterval(tickRef.current); }, []);

  const progress = ((timerSeconds - remaining) / timerSeconds) * 100;

  return (
    <div className="bg-white border border-[#E8E4DC] rounded-2xl overflow-hidden shadow-sm" data-testid={`mission-card-${mission.id}`}>
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1A2F4A] to-[#243a58] px-6 py-5 text-white">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold mb-1.5">Mission · {level}</p>
            <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="mission-title">
              <span className="text-3xl" aria-hidden="true">{mission.emoji}</span>
              {mission.title}
            </h2>
          </div>
          <button
            onClick={() => speak(`${mission.title}. ${mission.mission}`, 0.9)}
            className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-white text-[#1A2F4A] rounded-full px-4 py-2 text-xs font-bold transition-colors flex-shrink-0"
            data-testid="mission-listen-instructions"
          >
            <Volume2 className="w-3.5 h-3.5" /> Écouter
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-5 space-y-5">
        {/* Clue cards */}
        <div>
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[10px] text-[#6B4E37] uppercase tracking-[0.2em] font-bold">Indices</p>
            {randomClueMode && (
              <span className="text-[10px] text-[#D4AF37] font-bold tracking-wider" data-testid="random-clue-counter">
                {revealed} / {mission.clues.length}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="clue-cards">
            {mission.clues.map((c, i) => {
              const visible = i < revealed;
              return (
                <button
                  key={i}
                  onClick={() => visible && speak(c.t)}
                  disabled={!visible}
                  className={`group rounded-xl px-3 py-3 text-center transition-all border ${
                    visible
                      ? 'bg-[#FAF7F0] hover:bg-white border-[#E8E4DC] hover:border-[#D4AF37]'
                      : 'bg-[#F3F4F6] border-[#E8E4DC] opacity-50'
                  }`}
                  data-testid={`clue-${mission.id}-${i}`}
                >
                  <div className="text-2xl mb-1" aria-hidden="true">{visible ? c.e : '🔒'}</div>
                  <div className="text-[11px] font-medium text-[#1A2F4A] leading-tight">
                    {visible ? c.t : '— caché —'}
                  </div>
                </button>
              );
            })}
          </div>
          {randomClueMode && revealed < mission.clues.length && (
            <button
              onClick={() => setRevealed((r) => Math.min(r + 1, mission.clues.length))}
              className="w-full mt-2 inline-flex items-center justify-center gap-1.5 bg-white border border-[#D4AF37] text-[#1A2F4A] hover:bg-[#FAF7F0] rounded-full py-1.5 text-[11px] font-bold transition-colors"
              data-testid="reveal-next-clue"
            >
              <Sparkles className="w-3 h-3 text-[#D4AF37]" /> Révéler l'indice suivant
            </button>
          )}
        </div>

        {/* Mission prompt */}
        <div className="bg-[#FAF7F0] border-l-2 border-[#D4AF37] px-4 py-3 rounded-r-lg">
          <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.2em] font-bold mb-1">Ta mission</p>
          {mission.context && (
            <p className="text-[12px] text-[#6B4E37] italic leading-relaxed mb-1.5" data-testid="mission-context">
              {mission.context}
            </p>
          )}
          <p className="text-sm text-[#1A2F4A] leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="mission-prompt">
            {mission.mission}
          </p>
        </div>

        {/* Connector hints (A2 + B1) */}
        {CONNECTORS_BY_LEVEL[level] && (
          <div className="bg-white border border-[#E8E4DC] rounded-xl px-4 py-3" data-testid="connector-hints">
            <p className="text-[10px] text-[#6B4E37] uppercase tracking-[0.2em] font-bold mb-1.5">Connecteurs suggérés</p>
            <div className="flex flex-wrap gap-1.5">
              {CONNECTORS_BY_LEVEL[level].map((c) => (
                <span
                  key={c}
                  className="text-[10px] font-bold text-[#1A2F4A] bg-[#FAF7F0] border border-[#D4AF37]/30 rounded-full px-2.5 py-0.5"
                  data-testid={`connector-${c.replace(/[\s']+/g, '-')}`}
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Timer + Record controls */}
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs">
            <div className="inline-flex items-center gap-1.5 text-[#6B4E37]">
              <Timer className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span>{phase === 'recording' ? `${remaining}s restantes` : `${timerSeconds}s pour parler`}</span>
            </div>
            {phase === 'recording' && <span className="text-[#D4AF37] font-bold animate-pulse">● REC</span>}
          </div>
          <div className="w-full h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden" data-testid="mission-timer-bar">
            <div className="h-full rounded-full bg-[#D4AF37] transition-all duration-700" style={{ width: `${progress}%` }} />
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            {phase === 'idle' && (
              <button
                onClick={handleStart}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#1A2F4A] hover:bg-[#243a58] text-white rounded-full py-3 text-sm font-bold transition-colors"
                data-testid="mission-start"
              >
                <Mic className="w-4 h-4" /> Démarrer & m'enregistrer
              </button>
            )}
            {phase === 'recording' && (
              <button
                onClick={handleStopEarly}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white rounded-full py-3 text-sm font-bold animate-pulse"
                data-testid="mission-stop"
              >
                <Square className="w-4 h-4" /> Arrêter
              </button>
            )}
            {phase === 'done' && (
              <button
                onClick={handleReset}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-white border-2 border-[#1A2F4A] text-[#1A2F4A] hover:bg-[#FAF7F0] rounded-full py-3 text-sm font-bold transition-colors"
                data-testid="mission-retry"
              >
                <RotateCcw className="w-4 h-4" /> Réessayer
              </button>
            )}
          </div>

          {audioUrl && (
            <audio controls src={audioUrl} className="w-full h-9" data-testid="mission-playback" />
          )}
        </div>

        {/* AI feedback dashboard */}
        {feedback && (
          <div className="bg-[#1A2F4A] rounded-2xl p-5 space-y-4" data-testid="feedback-panel">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <p className="text-xs text-[#D4AF37] uppercase tracking-[0.2em] font-bold">Feedback IA · simulation</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Prononciation', value: feedback.pronun },
                { label: 'Fluidité', value: feedback.fluid },
                { label: 'Vocabulaire', value: feedback.vocab },
                { label: 'Grammaire', value: feedback.grammar },
              ].map((m) => (
                <div key={m.label}>
                  <div className="flex justify-between text-[10px] text-white/70 mb-1 font-medium">
                    <span>{m.label}</span>
                    <span className="text-[#D4AF37] font-bold">{m.value}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-[#D4AF37] transition-all duration-700" style={{ width: `${m.value}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <div>
                <p className="text-[10px] text-emerald-300 uppercase tracking-wider font-bold mb-1">Tes points forts</p>
                <ul className="text-xs text-white/85 space-y-1 list-disc pl-4">
                  {feedback.strengths.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
              <div>
                <p className="text-[10px] text-amber-300 uppercase tracking-wider font-bold mb-1">À travailler</p>
                <ul className="text-xs text-white/85 space-y-1 list-disc pl-4">
                  {feedback.tips.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
            </div>
            <div className="bg-[#D4AF37]/15 border border-[#D4AF37]/40 rounded-xl px-3 py-3">
              <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-bold mb-1">Alternative naturelle</p>
              <p className="text-xs text-white/90 italic leading-relaxed" data-testid="feedback-alternative">{mission.nativeAlt}</p>
            </div>
          </div>
        )}

        {/* Follow-up panel — appears for any mission that defines a followUpType (B1 + B2) */}
        {feedback && mission.followUpType && (
          <FollowUpPanel mission={mission} />
        )}

        {/* Send to teacher */}
        {feedback && (
          <div className="pt-1">
            {!submitted ? (
              <>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !audioBlob}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#1A2F4A] text-[#1A2F4A] hover:text-[#D4AF37] rounded-full py-2.5 text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-to-teacher"
                >
                  <Send className="w-3.5 h-3.5" />
                  {submitting ? 'Envoi en cours…' : 'Envoyer à mon professeur'}
                </button>
                {submitError && (
                  <p className="text-[11px] text-red-600 mt-1.5" data-testid="submit-error">{submitError}</p>
                )}
              </>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 rounded-full py-2.5 text-center text-xs font-bold text-emerald-700" data-testid="submit-confirm">
                ✓ Envoyé à ton professeur — tu recevras un retour bientôt.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* -----------------------------------------------------------
   B1 follow-up panel — single record extension prompted by AI
----------------------------------------------------------- */
function FollowUpPanel({ mission }) {
  const labels = FOLLOWUP_LABELS[mission.followUpType] || FOLLOWUP_LABELS.opinion;
  const [opened, setOpened] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | recording | done
  const [feedback, setFeedback] = useState(null);
  const { recording, audioUrl, start, stop, reset } = useRecorder();

  // Reset whenever mission changes (parent remount handles full reset too)
  useEffect(() => {
    setOpened(false);
    setPhase('idle');
    setFeedback(null);
    reset();
    if (recording) stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mission.id]);

  const handleStart = async () => {
    setFeedback(null);
    reset();
    const ok = await start();
    if (!ok) return;
    setPhase('recording');
  };

  const handleStop = () => {
    stop();
    setPhase('done');
    setFeedback(makeFeedback('B1'));
  };

  if (!opened) {
    return (
      <div className="bg-white border border-[#D4AF37]/40 rounded-2xl px-5 py-4" data-testid="followup-panel">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.2em] font-bold mb-0.5" data-testid="followup-eyebrow">
              Continuer l'échange · {labels.eyebrow}
            </p>
            <p className="text-sm text-[#1A2F4A] leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="followup-prompt">
              « {mission.followUpPrompt} »
            </p>
          </div>
        </div>
        <button
          onClick={() => setOpened(true)}
          className="w-full mt-4 inline-flex items-center justify-center gap-2 bg-[#1A2F4A] hover:bg-[#243a58] text-white rounded-full py-2.5 text-xs font-bold transition-colors"
          data-testid="followup-open"
        >
          <MessageCircle className="w-3.5 h-3.5" /> {labels.cta}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#D4AF37]/40 rounded-2xl px-5 py-4 space-y-3" data-testid="followup-panel-open">
      <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.2em] font-bold">
        Continuer l'échange · {labels.eyebrow}
      </p>
      <p className="text-sm text-[#1A2F4A] leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
        « {mission.followUpPrompt} »
      </p>
      <button
        onClick={() => speak(mission.followUpPrompt, 0.95)}
        className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#1A2F4A] text-[#1A2F4A] hover:text-[#D4AF37] rounded-full px-3 py-1 text-[11px] font-bold transition-colors"
        data-testid="followup-listen"
      >
        <Volume2 className="w-3 h-3" /> Écouter la question
      </button>

      <div className="flex flex-col sm:flex-row gap-2">
        {phase === 'idle' && (
          <button
            onClick={handleStart}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#1A2F4A] hover:bg-[#243a58] text-white rounded-full py-2.5 text-xs font-bold transition-colors"
            data-testid="followup-start"
          >
            <Mic className="w-3.5 h-3.5" /> M'enregistrer
          </button>
        )}
        {phase === 'recording' && (
          <button
            onClick={handleStop}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white rounded-full py-2.5 text-xs font-bold animate-pulse"
            data-testid="followup-stop"
          >
            <Square className="w-3.5 h-3.5" /> Arrêter
          </button>
        )}
        {phase === 'done' && (
          <button
            onClick={() => { setPhase('idle'); setFeedback(null); reset(); }}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-white border-2 border-[#1A2F4A] text-[#1A2F4A] hover:bg-[#FAF7F0] rounded-full py-2.5 text-xs font-bold transition-colors"
            data-testid="followup-retry"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Réessayer
          </button>
        )}
      </div>

      {audioUrl && <audio controls src={audioUrl} className="w-full h-9" data-testid="followup-playback" />}

      {feedback && (
        <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl px-3 py-3" data-testid="followup-feedback">
          <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-bold mb-1.5">Retour rapide</p>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-[#1A2F4A]">
            <div className="flex justify-between"><span>Argumentation</span><span className="font-bold text-[#D4AF37]">{feedback.grammar}%</span></div>
            <div className="flex justify-between"><span>Fluidité</span><span className="font-bold text-[#D4AF37]">{feedback.fluid}%</span></div>
            <div className="flex justify-between"><span>Vocabulaire</span><span className="font-bold text-[#D4AF37]">{feedback.vocab}%</span></div>
            <div className="flex justify-between"><span>Prononciation</span><span className="font-bold text-[#D4AF37]">{feedback.pronun}%</span></div>
          </div>
          <p className="text-[11px] text-[#6B4E37] italic mt-2 leading-relaxed">{feedback.tips[0]}</p>
        </div>
      )}
    </div>
  );
}

/* ===========================================================
   MAIN PAGE
=========================================================== */
export default function ParleAvecDesIndices() {
  const [level, setLevel] = useState('A1');
  const missions = MISSIONS_BY_LEVEL[level];
  const timerModes = TIMER_MODES_BY_LEVEL[level];

  const [activeId, setActiveId] = useState(missions[0].id);
  const [mode, setMode] = useState(timerModes[1].id); // default = middle option (Normal / Challenge)
  const [streak, setStreak] = useState(() => loadStreak());
  const [randomClueMode, setRandomClueMode] = useState(false);

  // When switching level, reset active mission + timer mode to that level's defaults
  useEffect(() => {
    const next = MISSIONS_BY_LEVEL[level];
    const nextModes = TIMER_MODES_BY_LEVEL[level];
    setActiveId(next[0].id);
    setMode(nextModes[1].id);
  }, [level]);

  const active = missions.find((m) => m.id === activeId) || missions[0];
  const timerSeconds = (timerModes.find((t) => t.id === mode) || timerModes[1]).seconds;

  const handleRandom = () => {
    const idx = Math.floor(Math.random() * missions.length);
    setActiveId(missions[idx].id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleComplete = () => {
    setStreak(bumpStreak());
  };

  return (
    <div className="bg-[#FAF7F0] min-h-screen">
      {/* Header */}
      <section className="pt-12 pb-6">
        <div className="max-w-[760px] mx-auto px-6">
          <div className="flex items-center gap-1.5 text-[10px] text-[#999] mb-6 flex-wrap" data-testid="breadcrumb">
            <Link to="/speaking-lab" className="hover:text-[#D4AF37] transition-colors">Speaking Lab</Link>
            <ChevronRight className="w-3 h-3" />
            <span>S'exprimer</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#1A2F4A] font-bold">Parle avec des indices</span>
          </div>

          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-10 bg-[#D4AF37]/50" />
              <MessageCircle className="w-5 h-5 text-[#D4AF37]" />
              <div className="h-px w-10 bg-[#D4AF37]/50" />
            </div>
            <h1
              className="text-3xl sm:text-4xl font-bold text-[#1A2F4A] mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="page-title"
            >
              Parle avec des indices
            </h1>
            <p className="text-sm text-[#6B4E37] italic max-w-md mx-auto leading-relaxed">
              {{
                A1: '20 missions orales pour construire des réflexes de spontanéité — sans peur, sans grammaire pesante.',
                A2: '20 missions A2 — raconte, explique, donne ton avis et connecte tes idées comme un vrai francophone.',
                B1: '20 missions B1 — argumente, nuance et continue l\'échange comme un francophone avancé.',
                B2: '20 missions B2 — discute des idées comme un francophone instruit : nuance, contre-arguments, conséquences à long terme.',
              }[level]}
            </p>
            <div className="flex justify-center mt-4 items-center gap-3 flex-wrap">
              <div className="flex" data-testid="level-tabs">
                {LEVELS.map((lv, idx) => {
                  const isActive = level === lv;
                  const isFirst = idx === 0;
                  const isLast = idx === LEVELS.length - 1;
                  return (
                    <button
                      key={lv}
                      onClick={() => setLevel(lv)}
                      className={`text-[10px] font-bold tracking-wider px-3 py-1 transition-colors border ${
                        isActive
                          ? 'bg-[#1A2F4A] text-white border-[#1A2F4A]'
                          : 'bg-white text-[#1A2F4A] border-[#E8E4DC] hover:border-[#D4AF37]'
                      } ${isFirst ? 'rounded-l-full' : ''} ${isLast ? 'rounded-r-full' : ''} ${!isFirst ? '-ml-px' : ''}`}
                      data-testid={`level-${lv}`}
                    >
                      {lv}
                    </button>
                  );
                })}
              </div>
              <div className="inline-flex items-center gap-1.5 bg-white border border-[#D4AF37]/40 rounded-full px-3 py-1" data-testid="streak-indicator">
                <Flame className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span className="text-[10px] font-bold text-[#1A2F4A] tracking-wider">
                  {streak.count} {streak.count === 1 ? 'jour' : 'jours'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Controls: mode + random */}
      <section className="pb-2">
        <div className="max-w-[760px] mx-auto px-6">
          <div className="bg-white border border-[#E8E4DC] rounded-2xl px-4 py-3 flex flex-col gap-3" data-testid="mission-controls">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="flex-1">
                <p className="text-[10px] text-[#6B4E37] uppercase tracking-[0.2em] font-bold mb-1.5">Mode du chrono</p>
                <div className="grid grid-cols-3 gap-1.5" data-testid="timer-modes">
                  {timerModes.map((t) => {
                    const isActive = mode === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => setMode(t.id)}
                        className={`rounded-full text-[11px] font-bold py-1.5 border transition-colors ${
                          isActive
                            ? 'bg-[#1A2F4A] text-white border-[#1A2F4A]'
                            : 'bg-white text-[#1A2F4A] border-[#E8E4DC] hover:border-[#D4AF37]'
                        }`}
                        data-testid={`timer-mode-${t.id}`}
                      >
                        {t.name} <span className="opacity-70 font-medium">· {t.sub}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="sm:border-l sm:border-[#E8E4DC] sm:pl-3">
                <button
                  onClick={handleRandom}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#1A2F4A] text-[#1A2F4A] hover:text-[#D4AF37] rounded-full px-4 py-2 text-xs font-bold transition-colors"
                  data-testid="mission-random"
                >
                  <Shuffle className="w-3.5 h-3.5" /> Mission aléatoire
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-[#F3F4F6]">
              <div>
                <p className="text-[11px] font-bold text-[#1A2F4A] leading-tight">Mode indices progressifs</p>
                <p className="text-[10px] text-[#6B4E37] italic leading-tight">Découvre tes indices un par un pendant que tu parles.</p>
              </div>
              <button
                onClick={() => setRandomClueMode((v) => !v)}
                className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${
                  randomClueMode ? 'bg-[#D4AF37]' : 'bg-[#E8E4DC]'
                }`}
                aria-pressed={randomClueMode}
                data-testid="random-clue-toggle"
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                    randomClueMode ? 'translate-x-5' : ''
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Active mission */}
      <section className="pb-8">
        <div className="max-w-[760px] mx-auto px-6 mt-4">
          <MissionCard
            mission={active}
            timerSeconds={timerSeconds}
            onComplete={handleComplete}
            level={level}
            randomClueMode={randomClueMode}
          />
        </div>
      </section>

      {/* Mission grid (20 tiles) */}
      <section className="pb-20">
        <div className="max-w-[760px] mx-auto px-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] text-[#6B4E37] uppercase tracking-[0.25em] font-bold">Toutes les missions {level}</p>
            <p className="text-[10px] text-[#999]">{missions.length} missions</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="mission-grid">
            {missions.map((m, idx) => {
              const isActive = m.id === activeId;
              return (
                <button
                  key={m.id}
                  onClick={() => {
                    setActiveId(m.id);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`group rounded-xl px-3 py-3 text-left transition-all border ${
                    isActive
                      ? 'bg-[#1A2F4A] text-white border-[#1A2F4A]'
                      : 'bg-white text-[#1A2F4A] border-[#E8E4DC] hover:border-[#D4AF37] hover:shadow-sm'
                  }`}
                  data-testid={`mission-tile-${m.id}`}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-xl flex-shrink-0" aria-hidden="true">{m.emoji}</span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-[9px] uppercase tracking-wider font-bold ${isActive ? 'text-[#D4AF37]' : 'text-[#999]'}`}>
                        Mission {idx + 1}
                      </p>
                      <p className="text-[11px] font-medium leading-tight mt-0.5 truncate">{m.title}</p>
                    </div>
                    {isActive && <Play className="w-3 h-3 text-[#D4AF37] mt-0.5 flex-shrink-0" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
